import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import {
  type Encounter,
  type UpdateEncounterNoteRequest as GeneratedUpdateEncounterNoteRequest,
  type UpdateEncounterNoteResponse as GeneratedUpdateEncounterNoteResponse,
  getGetEncounterByIdQueryKey,
  updateEncounterNote,
} from "@elation/api-client/src/stitch-web";

import { is409Error } from "@src/shared/utils/http/errors";
import { events } from "@src/shared/utils/observability/events";
import { captureException } from "@src/shared/utils/observability/sentry";

import type {
  UpdateEncounterNoteRequest,
  UpdateEncounterNoteResponse,
} from "../domain/encounter-note.types";

type CachedEncounter = { response?: unknown; data: Encounter };

export type UseEncounterNoteSaveOptions = {
  patientId?: number;
};

function toDomainResponse(
  result: GeneratedUpdateEncounterNoteResponse,
): UpdateEncounterNoteResponse {
  return {
    note: result.note,
    noteRevision: result.noteRevision,
  };
}

/**
 * Saves an encounter note revision locally and patches the encounter query cache.
 */
export function useEncounterNoteSave(
  encounterId: number | undefined,
  options: UseEncounterNoteSaveOptions = {},
) {
  const queryClient = useQueryClient();
  const { patientId } = options;

  const patchEncounterCache = useCallback(
    (result: UpdateEncounterNoteResponse) => {
      if (encounterId == null) return;

      queryClient.setQueryData<CachedEncounter>(
        getGetEncounterByIdQueryKey(encounterId),
        (old) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: {
              ...old.data,
              note: result.note,
              noteRevision: result.noteRevision,
            },
          };
        },
      );
    },
    [encounterId, queryClient],
  );

  const save = useCallback(
    async (
      input: UpdateEncounterNoteRequest,
    ): Promise<UpdateEncounterNoteResponse> => {
      if (encounterId == null) {
        throw new Error("Cannot save note without an encounter id");
      }

      try {
        const { data } = await updateEncounterNote(
          encounterId,
          input as GeneratedUpdateEncounterNoteRequest,
        );
        const result = toDomainResponse(data);
        events.saveNote.track({
          encounter_id: encounterId,
          patient_id: patientId,
          note_revision: result.noteRevision,
          success: true,
        });
        patchEncounterCache(result);
        return result;
      } catch (error) {
        events.saveNote.track({
          encounter_id: encounterId,
          patient_id: patientId,
          success: false,
          error: is409Error(error) ? "conflict" : "save_failed",
          error_code: is409Error(error) ? "conflict" : undefined,
        });
        if (!is409Error(error)) {
          captureException(error, {
            tags: { feature: "encounters", action: "save_note" },
            extra: { encounterId, patientId },
          });
        }
        throw error;
      }
    },
    [encounterId, patchEncounterCache, patientId],
  );

  return { save };
}

/**
 * Reads note revision from an encounter payload.
 */
export function readEncounterNoteRevision(
  encounter: Encounter | null | undefined,
): {
  noteRevision: number;
} {
  return {
    noteRevision:
      typeof encounter?.noteRevision === "number" ? encounter.noteRevision : 0,
  };
}
